'use client'

import { signIn } from 'next-auth/react'
import { useState, type KeyboardEvent } from 'react'

export default function LoginForm () {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [errorLogin, setErrorLogin] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errorKey, setErrorKey] = useState(0)
  const [capsLock, setCapsLock] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleLogin = async () => {
    setLoading(true)
    try {
      const res = await signIn('credentials', { username, password, redirect: false })

      if (res?.error) {
        setErrorLogin(true)
        setErrorKey(k => k + 1)
      } else if (res?.ok) {
        setSuccess(true)
        await new Promise(resolve => setTimeout(resolve, 600))
        const url = new URL(window.location.href)
        const callbackUrl = url.searchParams.get('callbackUrl')
        window.location.href = callbackUrl ?? '/dashboard/tasks'
      } else {
        setErrorLogin(true)
        setErrorKey(k => k + 1)
      }
    } catch (error) {
      setErrorLogin(true)
      setErrorKey(k => k + 1)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { void handleLogin() }
  }

  const handlePasswordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(e.getModifierState('CapsLock'))
    if (e.key === 'Enter') { void handleLogin() }
  }

  const handlePasswordKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(e.getModifierState('CapsLock'))
  }

  return (
    <div className="w-full max-w-sm mx-auto">

      {/* Logo + título */}
      <div className="text-center mb-10">
        <h1 className="text-5xl font-semibold text-gray-900 tracking-tight">Bienvenido</h1>
        <p className="mt-2 text-lg text-gray-400">Ingresá tus credenciales para continuar</p>
      </div>

      {/* Campos */}
      <div className="space-y-3">

        {/* Usuario */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <input
            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
            type="text"
            placeholder="Usuario"
            value={username}
            onChange={(e) => { setUsername(e.target.value); setErrorLogin(false) }}
            onKeyDown={handleKeyDown}
            autoComplete="username"
            autoFocus
          />
        </div>

        {/* Contraseña */}
        <div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
              type={showPassword ? 'text' : 'password'}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrorLogin(false) }}
              onKeyDown={handlePasswordKeyDown}
              onKeyUp={handlePasswordKeyUp}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => { setShowPassword(v => !v) }}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {/* Caps Lock warning */}
          {capsLock && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
              <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Mayúsculas activadas
            </p>
          )}
        </div>

        {/* Error */}
        {errorLogin && (
          <div key={errorKey} className="animate-shake flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 px-3 py-2.5 rounded-xl">
            <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Usuario o contraseña incorrectos
          </div>
        )}

        {/* Botón */}
        <button
          className={`w-full py-3 px-4 text-white text-sm font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-sm mt-1 disabled:cursor-not-allowed
            ${success
              ? 'bg-green-500 shadow-green-200'
              : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700 hover:shadow-md hover:shadow-blue-200 disabled:opacity-50'
            }`}
          onClick={() => { void handleLogin() }}
          disabled={loading || success || !username || !password}
        >
          {success ? (
            <>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Bienvenido
            </>
          ) : loading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Ingresando...
            </>
          ) : (
            <>
              Ingresar
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
